import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice as a Freelancer in South Africa — SARS, VAT & Getting Paid',
      'description': 'A complete guide for South African freelancers: what to put on an invoice, SARS VAT rules (15%), full vs abridged tax invoices, and how to receive international payments.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-as-freelancer-south-africa',
      'datePublished': '2026-06-07',
      'dateModified': '2026-06-07',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do freelancers in South Africa have to charge VAT?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Only if your annual taxable supplies (income from VAT-rated goods and services) exceed R1 million in any consecutive 12-month period. Once you cross R1 million you must register for VAT with SARS and charge 15% on all standard-rated supplies. You can register voluntarily once your taxable supplies exceed R50,000 in the past 12 months.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between a full tax invoice and an abridged tax invoice in South Africa?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A full tax invoice is required for any supply over R5,000. It must include the words "Tax Invoice", your name and address, your VAT registration number, the recipient\'s name and VAT number (if they are a vendor), the invoice number and date, a description of goods or services, quantities, the VAT amount, and the total including VAT. An abridged tax invoice is for supplies between R50 and R5,000 and has fewer requirements — it does not need the recipient\'s details.' },
        },
        {
          '@type': 'Question',
          'name': 'What must a South African freelance invoice include?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A standard South African freelance invoice (for non-VAT registered freelancers) must include: your full name or trading name, your contact details, the client name and address, a unique sequential invoice number, the invoice date, a description of services, the rand amount due, your payment terms and due date, and your banking details (bank name, account number, branch code). If you are VAT registered, also include your VAT registration number and show the 15% VAT amount separately.' },
        },
        {
          '@type': 'Question',
          'name': 'How do South African freelancers receive international payments?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The most popular methods are Payoneer (widely used on Upwork and Fiverr, withdraw to a South African bank in ZAR), Wise (receive USD, GBP, or EUR and convert to ZAR at competitive rates), and SWIFT bank wire (all major South African banks — FNB, Absa, Standard Bank, Nedbank — accept international transfers). Services exported to non-resident clients are generally zero-rated for VAT (0%), meaning you do not charge 15% VAT on international invoices.' },
        },
        {
          '@type': 'Question',
          'name': 'Do South African freelancers need to pay income tax?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. South African freelancers are subject to income tax on their profits at the personal income tax rates (18%–45% on a sliding scale for individuals). You must register as a provisional taxpayer with SARS if you earn more than R30,000 per year from non-employment income and pay provisional tax twice a year (August and February). Keep records of all invoices and expenses for at least 5 years.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your full name or trading name', note: 'Sole traders use their legal name; close corporations or PTYs use the registered name' },
  { field: 'Your contact details', note: 'Address, phone number, and email' },
  { field: "Client's name and billing address", note: 'Full name and address of the person or company being billed' },
  { field: 'A unique sequential invoice number', note: 'e.g. INV-2026-001 — must be sequential and never reused' },
  { field: 'Invoice date and payment due date', note: 'SARS requires VAT invoices to be issued within 21 days of the supply' },
  { field: 'Description of services provided', note: 'Specific enough for your client to match it to the work agreed' },
  { field: 'Amount (exclusive of VAT if VAT registered)', note: 'Show net amount, VAT amount (15%), and total gross amount separately' },
  { field: 'Your VAT registration number (if VAT registered)', note: 'Required on all tax invoices; available from your SARS eFiling profile' },
  { field: "Recipient's VAT number (if over R5,000)", note: 'Required on full tax invoices for client-vendor supplies over R5,000' },
  { field: 'Your South African bank details', note: 'Bank name, account holder name, account number, and branch code (6 digits)' },
];

export default function HowToInvoiceFreelancerSouthAfricaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice as a Freelancer in South Africa — SARS, VAT & Getting Paid | Invilic"
        description="Complete guide for South African freelancers: invoice requirements, SARS VAT rules, 15% VAT registration, full vs abridged tax invoices, and how to receive international payments."
        canonical="https://www.invilic.com/blog/how-to-invoice-as-freelancer-south-africa"
        ogType="article"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
              South Africa
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">7 min read · June 7, 2026</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">
            How to Invoice as a Freelancer in South Africa — SARS, VAT & Getting Paid
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 leading-relaxed">
            South Africa has straightforward invoicing rules — but SARS has specific requirements for tax invoices, VAT registration thresholds, and how long to keep your records. This guide covers everything a South African freelancer needs to know.
          </p>
        </div>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">What Must Be on a South African Freelance Invoice?</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            SARS and the VAT Act specify what must appear on a tax invoice. For non-VAT-registered freelancers, requirements are lighter — but best practice is to include everything below from the start.
          </p>
          <div className="space-y-3">
            {invoiceFields.map(({ field, note }) => (
              <div key={field} className="flex items-start gap-3 p-4 rounded-xl border border-yellow-100 dark:border-yellow-800/30 bg-yellow-50 dark:bg-yellow-900/10">
                <CheckCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{field}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">SARS VAT Rules for Freelancers</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            VAT in South Africa is administered by SARS (South African Revenue Service). The standard rate is <strong className="text-gray-900 dark:text-white">15%</strong>. Here is when it applies to you:
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Below R50,000/year', badge: 'No VAT required', color: 'gray', detail: 'No VAT registration needed. Invoice the full amount with no VAT line.' },
              { label: 'R50,000–R999,999/year', badge: 'Voluntary VAT', color: 'yellow', detail: 'You may register for VAT voluntarily, which lets you claim input VAT on expenses.' },
              { label: 'Over R1,000,000/year', badge: 'Mandatory VAT', color: 'red', detail: 'You must register for VAT with SARS and charge 15% on all standard-rated supplies.' },
            ].map(({ label, badge, color, detail }) => (
              <div key={label} className={`p-4 rounded-xl border border-${color}-200 dark:border-${color}-800/40 bg-${color}-50 dark:bg-${color}-900/20`}>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                <p className={`font-bold text-${color}-700 dark:text-${color}-300 text-sm mb-2`}>{badge}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
          <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-white">21-day rule:</strong> VAT-registered freelancers must issue a tax invoice within 21 days of the date of supply (when the service was performed or the goods delivered).
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Full Tax Invoice vs Abridged Tax Invoice</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            SARS distinguishes between three types of tax invoices. Which one you issue depends on the invoice amount:
          </p>
          <div className="space-y-4">
            {[
              {
                type: 'No tax invoice required',
                threshold: 'Under R50',
                detail: 'A till slip or sales docket is sufficient. Must show VAT charged.',
                color: 'gray',
              },
              {
                type: 'Abridged Tax Invoice',
                threshold: 'R50 to R5,000',
                detail: 'Must include: "Tax Invoice", your name and VAT number, invoice date, description, VAT rate, VAT amount, and total including VAT. Does NOT need the recipient\'s name or VAT number.',
                color: 'yellow',
              },
              {
                type: 'Full Tax Invoice',
                threshold: 'Over R5,000',
                detail: 'Must include all abridged fields PLUS the recipient\'s name, address, and VAT registration number (if they are a VAT vendor). This is the standard B2B invoice for South African freelancers.',
                color: 'green',
              },
            ].map(({ type, threshold, detail, color }) => (
              <div key={type} className={`p-5 rounded-xl border border-${color}-200 dark:border-${color}-800/40 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`font-bold text-${color}-700 dark:text-${color}-300`}>{type}</p>
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900/50 px-2 py-0.5 rounded-lg border border-gray-200 dark:border-gray-700">{threshold}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How to Receive International Payments</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            South African freelancers billing overseas clients should note: services exported to non-resident clients are generally <strong className="text-gray-900 dark:text-white">zero-rated for VAT (0%)</strong> — you do not charge 15% VAT on international invoices. Invoice in the client's preferred currency.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { method: 'Payoneer', detail: 'The most popular option for South African freelancers on Upwork, Fiverr, and Toptal. Receive USD, EUR, or GBP and withdraw to your South African bank (FNB, Absa, Standard Bank, Nedbank) in ZAR.' },
              { method: 'Wise', detail: 'Receive USD, GBP, or EUR at the mid-market rate. Significantly lower fees than traditional bank wire for international transfers.' },
              { method: 'SWIFT bank wire', detail: 'All major South African banks accept SWIFT transfers. Give your client your bank name, SWIFT code, account number, and branch code (6 digits).' },
              { method: 'PayPal', detail: 'Available in South Africa. Functional for billing international clients, though fees are higher than Wise or Payoneer. Withdrawals go to your local bank in ZAR.' },
            ].map(({ method, detail }) => (
              <div key={method} className="p-4 rounded-xl border border-yellow-100 dark:border-yellow-800/30 bg-yellow-50 dark:bg-yellow-900/10">
                <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{method}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Income Tax and Provisional Tax</h2>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <p>South African freelancers are taxed on their net profit (income minus allowable expenses) at the personal income tax rates — from 18% on the first R226,000 up to 45% above R1.8 million (2024/25 rates).</p>
            <p>If you earn more than R30,000 per year from non-employment sources, you must register as a <strong className="text-gray-900 dark:text-white">provisional taxpayer</strong> and pay provisional tax in two installments (the first by end of August, the second by end of February).</p>
            <p>Allowable business expenses include home office costs, software subscriptions, professional development, and business travel. Keep all invoices and receipts for at least <strong className="text-gray-900 dark:text-white">5 years</strong> — SARS can audit this period.</p>
            <p>Register on <strong className="text-gray-900 dark:text-white">SARS eFiling</strong> (efiling.sars.gov.za) to manage your income tax, VAT, and provisional tax returns online.</p>
          </div>
        </section>

        <BlogCTA
          heading="Create a South African Invoice for Free"
          body="Professional PDF invoices in ZAR, USD, or any currency. No account required."
          linkTo="/invoice-generator-south-africa"
          linkText="Use South Africa Invoice Generator"
        />

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <RelatedArticles current="/blog/how-to-invoice-as-freelancer-south-africa" />
      </main>
    </div>
  );
}
